import { FC, ReactElement } from 'react'
import styled from 'styled-components'
import { BsEye } from "react-icons/bs";
import { IoIosPeople } from 'react-icons/io'
import { device } from '../breakpoints'
import { IconType, } from 'react-icons'

import { GiDiamondTrophy } from 'react-icons/gi'
import { GiSmartphone } from 'react-icons/gi'
import { GiPiggyBank } from 'react-icons/gi'
import { GiClockwork } from 'react-icons/gi'
import { GiModernCity } from 'react-icons/gi'
import { GiFactory } from 'react-icons/gi'
import { GiLightBulb } from 'react-icons/gi'
import { GiShakingHands } from 'react-icons/gi'
import { GiAbstract086 } from 'react-icons/gi'
import { GiBed } from "react-icons/gi";

const sectorsIcons: { [key: string]: IconType } = {
  Technologie: GiLightBulb,
  Luxe: GiDiamondTrophy,
  Digital: GiSmartphone,
  Services: GiShakingHands,
  Industrie: GiFactory,
  Immobilier: GiModernCity,
  Finance: GiPiggyBank,
  Horlogerie: GiClockwork,
  'Hôtellerie restauration': GiBed,
}

const sectors = [
  'Technologies',
  'Industrie',
  'Finance',
  'Immobilier',
  'Services',
  'Digital',
]

interface ListItemProps {
  id: string
  lastName: string
  name: string
  gender: string
  imageElvisId: string
  imageFocusPointX: string
  imageFocusPointY: string
  imageCropOffsetX: string
  imageCropOffsetY: string
  imageCropWidth: string
  imageCropHeight: string
  businessName: string
  age: number
  location: string
  text: string
  mainSector: string
  links: any
}

const ListItem: FC<ListItemProps> = (props): ReactElement => {
  return (
    <Container href={`https://www.bilan.ch/${props.id}`} target='_blank'>
      <Content>
        <Head>
          <ImageContainer
            imageUrl={
              props.imageElvisId
                ? `https://prod-images.unitycms.io/api/filerepo/files/${props.imageElvisId}/data?cropWidth=${props.imageCropWidth}&cropHeight=${props.imageCropHeight}&cropOffsetX=${props.imageCropOffsetX}&cropOffsetY=${props.imageCropOffsetY}&focusPointX=${props.imageFocusPointX}&focusPointY=${props.imageFocusPointY}&maxHeight=160`
                : require(`../../Images/placeholder-m.png`)
            }
          />
          <HeadText>
            <Title>{props.name}</Title>
            <Location>
              {props.age} ans <span style={{ opacity: 0.15 }}>|</span>{' '}
              {props.location}
            </Location>
          </HeadText>
        </Head>

        <BusinessContainer>
          <BusinessSection>
            <BusinessName>
              <>{IoIosPeople({})}</>
              <BusinessText>{props.businessName}</BusinessText>
            </BusinessName>
          </BusinessSection>
          <BusinessSection>
            <Sector>
              {sectorsIcons[props.mainSector] ? (
                <>{sectorsIcons[props.mainSector]({})}</>
              ) : (
                <>{GiAbstract086({})}</>
              )}
              <BusinessText>{props.mainSector}</BusinessText>
            </Sector>
          </BusinessSection>
        </BusinessContainer>

        <Description>{props.text}</Description>

        <Foot>
          <ReadItLink>Lire la suite</ReadItLink>
          <IconArrowContainer>
            <>{BsEye({})}</>
          </IconArrowContainer>
        </Foot>
      </Content>
    </Container>
  )
}

const HeadText = styled.div`
  margin-left: 20px;
  padding-right: 15px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const ReadItLink = styled.div`
  position: relative;
  display: inline-block;
  font-family: var(--font-sans-serif);
  color: ${({ theme }) => theme.textColor};
  font-weight: 600;
  -webkit-font-smoothing: auto;

  &:after {
    content: '';
    width: 0%;
    background: var(--tenant-${({ theme }) => theme.tenant.key.slice(0, 3)});
    height: 3px;
    display: block;
    transform: scaleZ(0);
    transition: width 0.3s ease-in-out;
  }
`

const Container = styled.a`
  font-family: var(--font-serif);
  color: ${({ theme }) => theme.textColor};
  font-size: 1.8rem;
  line-height: 3rem;
  cursor: pointer;
  position: relative;
  min-width: 0;
  -webkit-font-smoothing: antialiased;
  text-decoration: none;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    padding: 9px 4px;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    padding: 12px;
  }
`

const BusinessContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.lineColor};
  border-left: 0;
  border-right: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: ${({ theme }) => theme.siteBackground};

  font-weight: 600;
  -webkit-font-smoothing: antialiased;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    font-weight: 400;
    -webkit-font-smoothing: auto;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    font-weight: 600;
    -webkit-font-smoothing: antialiased;
  }
`

const BusinessText = styled.div`
  margin-left: 10px;
  line-height: 1;

  small {
    font-size: 1.45rem;
    font-weight: 300;
  }
`

const BusinessSection = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  svg {
    font-size: 2rem;
    color: ${({ theme }) => theme.mainColor};
  }

  &:first-child {
    padding-top: 15px;

    &:after {
      content: '';
      position: absolute;
      top: 10%;
      right: 0;
      width: 1px;
      height: 80%;
      background: ${({ theme }) => theme.lineColor};
    }
  }

  &:last-child {
    border: 0;
    padding-bottom: 15px;
  }

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    padding: 15px;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    padding: 15px;
  }
`

const Content = styled.div`
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  transition: transform 0.2s ease;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.lineColor};
  cursor: pointer;

  &:hover {
    transform: scale(1.02);
  }
`

const Head = styled.div`
  display: flex;
  padding-bottom: 0;
  background: ${({ theme }) => theme.siteBackground};
`

const Foot = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  line-height: 1.2;
  background: ${({ theme }) => theme.siteBackground};
;
  cursor: pointer;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    padding: 15px;
    padding-top: 0px;
    padding-bottom: 10px;
    font-size: 1.6rem;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    padding: 15px;
    padding-top: 0px;
    padding-bottom: 10px;
    font-size: 1.45rem;
  }
`

const IconArrowContainer = styled.div`
  font-size: 2rem;
  background: ${({ theme }) => theme.mainColor};
  height: 30px;
  width: 30px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.siteBackground};
;
`

const Sector = styled.div`
  font-family: var(--font-sans-serif);
  display: flex;
  align-items: center;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    font-size: 1.5rem;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    font-size: 1.25rem;
  }
`

const BusinessName = styled.div`
  font-size: 1.45rem;
  font-family: var(--font-sans-serif);
  display: flex;
  align-items: center;

  svg {
    font-size: 2.25rem;
  }

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    font-size: 1.5rem;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    font-size: 1.25rem;
  }
`

const ImageContainer = styled.div.attrs<{ imageUrl: string }>(
  ({ imageUrl }) => ({
    style: {
      backgroundImage: `url(${imageUrl})`,
    },
  })
)<{ imageUrl: string }>`
  overflow: hidden;
  position: relative;
  background-size: cover;
  background-position: center;
  filter: grayscale(100%);

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    height: 90px;
    width: 90px;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    height: 90px;
    width: 90px;
  }
`

const Title = styled.div`
  font-family: var(--font-serif);
  -webkit-font-smoothing: antialiased;
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 2px;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    font-size: 2.1rem;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    font-size: 2.1rem;
  }

  @media ${device.laptopL} {
    font-size: 1.8rem;
  }
`

const Location = styled.div`
  font-family: var(--font-sans-serif);
  font-weight: 400;
  line-height: 1.2;
  color: ${({ theme }) => theme.textGrey};
  opacity: 0.8;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    font-size: 1.5rem;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    font-size: 1.25rem;
  }
`

const Description = styled.div`
  position: relative;
  font-family: var(--font-serif);
  color: ${({ theme }) => theme.textGrey};
  font-weight: 400;
  line-height: 1.4;
  height: 120px;
  overflow: hidden;
  background: ${({ theme }) => theme.siteBackground};
  -webkit-font-smoothing: auto;

  &:after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    background: ${({ theme }) => theme.siteBackground};
    width: 100%;
    height: 100%;
    z-index: 0;
    mask-image: linear-gradient(
      0deg,
      rgba(0, 0, 0, 1) 10%,
      rgba(0, 0, 0, 0) 90%
    );
  }

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    padding: 15px;
    padding-top: 10px;
    padding-bottom: 0;
    font-size: 1.7rem;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    padding: 15px;
    padding-top: 10px;
    padding-bottom: 0;
    font-size: 1.6rem;
  }
`

export default ListItem
